import * as React from "react";
import { useEffect, useState } from "react";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { makeStyles, shorthands } from "@fluentui/react-components";
import { FluentField } from "./Fluent-Field";
import {
  getFormattedDate,
  adjustDateForPicker,
  DateStringFormatBasedOnDepartment,
} from "../../util/time-helpers";
import {
  DateRangeType,
  getDateRangeArray,
} from "@fluentui/react-calendar-compat";
import PropTypes from "prop-types";
import "./fluent-overide.scss";

const useStyles = makeStyles({
  root: {
    "& .fui-CalendarDay": {
      "& table": {
        "& tr": {
          height: "0px",
          ...shorthands.border("none"),
        },
      },
    },
  },
});

// TAD-5641 — Coerce value into a Date, matching the legacy react-datepicker
// behavior which silently accepted Date instances, ISO strings, or Unix-ms
// numbers. Without coercion, consumers that pass a timestamp or string would
// render an empty picker (and the later `adjustDateForPicker` call would
// throw because numbers have no `.getTime()`).
const toDate = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) return isNaN(value) ? null : value;
  const parsed = new Date(value);
  return isNaN(parsed) ? null : parsed;
};

const withinBounds = (date, minDate, maxDate) =>
  (!minDate || date >= minDate) && (!maxDate || date <= maxDate);

export const FluentDatePicker = ({
  label,
  disabled,
  validationMessage,
  required,
  value,
  minDate,
  maxDate,
  placeholder,
  onChange,
  componentStyle,
  size,
  id,
  name,
  className,
  dateFormatID,
  formatDate,
  isInvalid,
  feedback,
  autoComplete = "off",
  // TAD-5692 — week-selection feature props (Dashboard / Historical Data Report)
  firstDayOfWeek,
  isWeekSelectionEnabled,
  dayOftheWeekSelection = 0,
  showWeekNumbers,
  ...rest
}) => {
  const styles = useStyles();
  const coercedValue = toDate(value);
  // TAD-5641 — minDate/maxDate must also be coerced. Some consumers pass
  // Date.parse(...) which returns a number, not a Date — Fluent's Calendar
  // internals call `.getDate()` on it and crash with
  // "TypeError: date.getDate is not a function" (seen on tasks-reassign-panel
  // where maxDate={Date.parse(task?.Project?.End)}). Coerce defensively so
  // the component tolerates timestamps/strings for bounds as well as value.
  const coercedMinDate = toDate(minDate) ?? undefined;
  const coercedMaxDate = toDate(maxDate) ?? undefined;
  const [lastValidValue, setLastValidValue] = useState(coercedValue);
  const dataTestId = rest["data-testid"];

  // TAD-5692 — assemble calendar props; week-selection mode adds row numbers
  // and a Week date-range type to the underlying Fluent Calendar.
  let calendarProps = { className: styles.root };
  if (isWeekSelectionEnabled) {
    calendarProps = {
      ...calendarProps,
      showWeekNumbers: true,
      dateRangeType: DateRangeType.Week,
    };
  }

  useEffect(() => {
    setLastValidValue(coercedValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coercedValue?.getTime?.()]);

  const handleDateChange = (date) => {
    // TAD-5692 — week-selection: pick the latest in-bounds candidate within
    // the week range, working backwards from dayOftheWeekSelection.
    if (isWeekSelectionEnabled) {
      const dateRange = getDateRangeArray(
        date,
        DateRangeType.Week,
        firstDayOfWeek
      );
      let selectedDate = null;
      for (let i = dayOftheWeekSelection; i >= 0; i--) {
        const candidate = dateRange[i];
        if (withinBounds(candidate, coercedMinDate, coercedMaxDate)) {
          selectedDate = candidate;
          break;
        }
      }
      date = selectedDate ?? lastValidValue;
    }

    const willAccept = !date || withinBounds(date, coercedMinDate, coercedMaxDate);
    if (willAccept) {
      setLastValidValue(date);
      onChange(date);
    } else {
      onChange(lastValidValue);
    }
  };

  const handleBlur = (e) => {
    const inputDate = new Date(e.target.value);
    if (!isNaN(inputDate) && withinBounds(inputDate, coercedMinDate, coercedMaxDate)) {
      setLastValidValue(inputDate);
    } else {
      onChange(lastValidValue);
    }
  };

  const resolvedFormatDate =
    formatDate ||
    (dateFormatID
      ? (d) =>
          d
            ? DateStringFormatBasedOnDepartment(
                d,
                dateFormatID === "en-GB" ? 1 : 0
              )
            : ""
      : getFormattedDate);

  const resolvedValidation =
    validationMessage ?? (isInvalid ? feedback : undefined);

  const mergedClassName = `fluentInput ${componentStyle || ""} ${
    className || ""
  }`.trim();

  return (
    <FluentField
      label={label}
      size={size}
      validationMessage={resolvedValidation}
      required={required}
    >
      <DatePicker
        id={id}
        name={name}
        placeholder={placeholder}
        formatDate={resolvedFormatDate}
        value={adjustDateForPicker(coercedValue)}
        minDate={coercedMinDate}
        maxDate={coercedMaxDate}
        onSelectDate={handleDateChange}
        calendar={calendarProps}
        onBlur={handleBlur}
        disabled={disabled}
        className={mergedClassName}
        allowTextInput={true}
        autoComplete={autoComplete}
        input={dataTestId ? { "data-testid": dataTestId } : undefined}
        firstDayOfWeek={firstDayOfWeek ?? 0}
        showWeekNumbers={showWeekNumbers ?? false}
      />
    </FluentField>
  );
};

FluentDatePicker.propTypes = {
  label: PropTypes.string,
  disabled: PropTypes.bool,
  validationMessage: PropTypes.string,
  required: PropTypes.bool,
  // TAD-5641 — accept Date instances, Unix-ms numbers, or ISO strings; all
  // are coerced to Date internally via toDate(). Matches legacy
  // react-datepicker's permissive value handling. Same applies to min/max
  // because some call sites use Date.parse(...) which returns a number.
  value: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.number,
    PropTypes.string,
  ]),
  minDate: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.number,
    PropTypes.string,
  ]),
  maxDate: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.number,
    PropTypes.string,
  ]),
  placeholder: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  componentStyle: PropTypes.string,
  size: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
  className: PropTypes.string,
  dateFormatID: PropTypes.string,
  formatDate: PropTypes.func,
  isInvalid: PropTypes.bool,
  feedback: PropTypes.string,
  autoComplete: PropTypes.string,
  // TAD-5692 — week-selection props
  firstDayOfWeek: PropTypes.number,
  isWeekSelectionEnabled: PropTypes.bool,
  dayOftheWeekSelection: PropTypes.number,
  showWeekNumbers: PropTypes.bool,
};
